import { useMemo, useRef, useState, useCallback } from 'react';
import { Box, Text, Badge } from '@chakra-ui/react';
import { ADMIN_COLORS, ADMIN_LAYOUT } from '../../../../../generated/tokens/constants';
import type { BlockDocument, PreviewMode } from '../editor/types';
import { BlockRenderer } from './BlockRenderer';
import { BlockErrorBoundary } from './BlockErrorBoundary';
import { BlockLayoutWrapper } from './BlockLayoutWrapper';
import { VisibilityWrapper } from './VisibilityWrapper';
import { buildSectionStyle, buildContentStyle, buildFlexContainerStyle, buildOverlayStyle, mergeResponsiveLayout, buildMobileContentSx, buildMobileFlexSx } from './utils/sectionStyleBuilder';
import { AnimationWrapper } from './AnimationWrapper';
import { SectionTypographyProvider } from './SectionTypographyContext';
import { EditorBlockContextProvider } from './EditorBlockContext';
import { usePageConfig } from './PageConfigContext';
import { useSpacingVizEnabled } from '../editor/state/spacingVizState';
import { SpacingVizActiveProvider } from './SpacingVizActiveContext';
import { SpacingOverlay } from '../editor/components/SpacingOverlay';
import { resolveSectionPadding, extractSectionPaddingTokenName } from '../editor/utils/resolveSpacingToPx';
import type { ResolvedSpacing } from '../editor/utils/resolveSpacingToPx';
import './styles/animations.css';

const SUPPORTED_DOCUMENT_VERSION = 1;
const ZERO_MARGIN: ResolvedSpacing = { top: 0, right: 0, bottom: 0, left: 0 };

interface ComposableSectionProps {
  sectionId: number;
  body: string;
  previewMode?: PreviewMode;
  /** Pre-resolved image map — passed from parent to avoid per-section requests */
  resolvedImageMap?: Record<number, { image_id: number; data: Record<string, string>; alt: string; error?: string }>;
  /** When true, spacing visualization overlays are suppressed (used in PreviewPane) */
  disableSpacingViz?: boolean;
}

/**
 * Renders a composable (block-based) content section.
 * Parses the body JSON as a BlockDocument, then iterates blocks
 * through VisibilityWrapper + BlockErrorBoundary + BlockRenderer.
 * M7: Also renders background, overlay, and layout from doc.config.
 */
export function ComposableSection({
  sectionId,
  body,
  previewMode,
  resolvedImageMap,
  disableSpacingViz,
}: ComposableSectionProps): React.ReactElement {
  // Spacing visualization overlay — only active in editor (when previewMode is set)
  // Suppressed entirely in PreviewPane (disableSpacingViz) and public pages (no previewMode)
  const rawSpacingVizEnabled = useSpacingVizEnabled();
  const spacingVizEnabled = !disableSpacingViz && rawSpacingVizEnabled && !!previewMode;
  // 'none' = mouse outside, 'direct' = hovering section background, 'child' = hovering a child block
  const [sectionHoverState, setSectionHoverState] = useState<'none' | 'direct' | 'child'>('none');

  const handleSectionMouseOver = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const isOverBlock = target.closest?.('[data-block-id]') != null;
      setSectionHoverState(isOverBlock ? 'child' : 'direct');
    },
    [],
  );

  const handleSectionMouseLeave = useCallback(
    () => setSectionHoverState('none'),
    [],
  );

  const parsed = useMemo(() => {
    try {
      const doc = JSON.parse(body) as BlockDocument;
      if (!doc || !Array.isArray(doc.blocks)) {
        return { error: 'Invalid document: blocks must be an array', doc: null };
      }
      return { error: null, doc };
    } catch (e) {
      return {
        error: `Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`,
        doc: null,
      };
    }
  }, [body]);

  // Error state — invalid JSON or malformed document
  if (parsed.error) {
    return (
      <Box
        gc-testing-id="sectionComposableError"
        p={ADMIN_LAYOUT.cardPadding}
        borderRadius={ADMIN_LAYOUT.radius}
        bg={ADMIN_COLORS.card}
        border="1px solid"
        borderColor="red.800"
      >
        <Badge colorScheme="red" fontSize="xs" mb={2}>
          Section Error
        </Badge>
        <Text fontSize="sm" color="red.300">
          {parsed.error}
        </Text>
        <Text fontSize="xs" color={ADMIN_COLORS.textMuted} mt={1}>
          Section ID: {sectionId}
        </Text>
      </Box>
    );
  }

  const doc = parsed.doc!;
  const config = doc.config;

  // M8: When in editor mobile preview, merge responsive overrides into layout
  const effectiveConfig = useMemo(() => {
    if (!config) return config;
    if (previewMode?.device === 'mobile' && config.responsive?.mobile) {
      const mergedLayout = mergeResponsiveLayout(config.layout, config.responsive.mobile);
      return { ...config, layout: mergedLayout };
    }
    return config;
  }, [config, previewMode?.device]);

  // Resolve page-level content_max_width for section inheritance
  const pageConfig = usePageConfig();
  const pageContentMaxWidth = pageConfig?.style?.content_max_width ?? undefined;

  // Build CSS styles from config — memoized with proper deps
  const sectionStyle = useMemo(
    () => buildSectionStyle(effectiveConfig, resolvedImageMap ?? {}),
    [effectiveConfig, resolvedImageMap],
  );
  const contentStyle = useMemo(
    () => buildContentStyle(effectiveConfig, pageContentMaxWidth),
    [effectiveConfig, pageContentMaxWidth],
  );
  const flexContainerStyle = useMemo(
    () => buildFlexContainerStyle(effectiveConfig),
    [effectiveConfig],
  );
  const overlayStyle = useMemo(
    () => buildOverlayStyle(config?.overlay),
    [config?.overlay],
  );

  // Production mobile media queries — only when not in editor preview (editor uses effectiveConfig merge)
  const mobileContentSx = useMemo(
    () => previewMode ? null : buildMobileContentSx(config, pageContentMaxWidth),
    [config, pageContentMaxWidth, previewMode],
  );
  const mobileFlexSx = useMemo(
    () => previewMode ? null : buildMobileFlexSx(config),
    [config, previewMode],
  );

  // Version mismatch warning
  const versionMismatch = doc.version > SUPPORTED_DOCUMENT_VERSION;

  // Empty state
  if (doc.blocks.length === 0) {
    return (
      <Box
        gc-testing-id="sectionComposableEmpty"
        p={ADMIN_LAYOUT.cardPadding}
        borderRadius={ADMIN_LAYOUT.radius}
        bg={ADMIN_COLORS.card}
        border="1px dashed"
        borderColor={ADMIN_COLORS.border}
        textAlign="center"
      >
        <Text fontSize="sm" color={ADMIN_COLORS.textMuted}>
          This section has no blocks yet.
        </Text>
      </Box>
    );
  }

  // Provide a minimal EditorBlockContext for blocks that need resolvedImageMap
  // (e.g. IconGridBlock). In preview mode we don't have editor callbacks,
  // so stub them with noops.
  const noopRef = useRef((_a: number, _b: string) => {});
  const previewEditorCtx = useMemo(() => ({
    sectionId,
    onSelectBlock: noopRef.current,
    selection: null,
    resolvedImageMap: resolvedImageMap ?? {},
    editingBlockId: null,
    setEditingBlock: () => {},
  }), [sectionId, resolvedImageMap]);

  const hasOverlay = config?.overlay && config.overlay.type !== 'none';

  // Spacing visualization — resolve section padding (effectiveConfig already has mobile merge)
  const sectionPaddingResolved = useMemo(
    () => resolveSectionPadding(effectiveConfig?.layout?.padding),
    [effectiveConfig?.layout?.padding],
  );
  const sectionPaddingTokens = useMemo(
    () => extractSectionPaddingTokenName(effectiveConfig?.layout?.padding),
    [effectiveConfig?.layout?.padding],
  );
  const showSpacingOverlay = spacingVizEnabled && sectionHoverState === 'direct';
  const showSectionOutline = spacingVizEnabled && sectionHoverState === 'child';

  return (
    <SpacingVizActiveProvider value={spacingVizEnabled}>
    <Box
      gc-testing-id="sectionComposable"
      data-gc-testing-id={`composable-section-${sectionId}`}
      data-section-id={sectionId}
      position="relative"
      overflow={spacingVizEnabled ? 'visible' : undefined}
      style={sectionStyle}
      onMouseOver={spacingVizEnabled ? handleSectionMouseOver : undefined}
      onMouseLeave={spacingVizEnabled ? handleSectionMouseLeave : undefined}
    >
      {versionMismatch && (
        <Box
          gc-testing-id="sectionVersionWarning"
          p={3}
          mb={3}
          borderRadius={ADMIN_LAYOUT.radius}
          bg="orange.900"
          border="1px solid"
          borderColor="orange.700"
          position="relative"
          zIndex={2}
        >
          <Text fontSize="sm" color="orange.200">
            This section uses document version {doc.version} but the renderer supports
            version {SUPPORTED_DOCUMENT_VERSION}. Some blocks may not render correctly.
          </Text>
        </Box>
      )}

      {/* Overlay layer */}
      {hasOverlay && (
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          style={overlayStyle}
          zIndex={0}
        />
      )}

      {/* Content layer — maxWidth/padding applied here, not on outer section */}
      <AnimationWrapper animation={config?.animation}>
        <SectionTypographyProvider value={config?.typography}>
        <EditorBlockContextProvider value={previewEditorCtx}>
        <Box position="relative" zIndex={1} style={contentStyle} sx={mobileContentSx ?? undefined}>
          <Box style={flexContainerStyle} sx={mobileFlexSx ?? undefined}>
          {doc.blocks.filter((block) => !block.is_hidden).map((block) => (
            <VisibilityWrapper
              key={block.id}
              rules={block.visibility}
              previewMode={previewMode}
            >
              <BlockErrorBoundary blockType={block.type} blockId={block.id}>
                <BlockLayoutWrapper layout={block.layout}>
                  <AnimationWrapper animation={block.animation?.stagger?.enabled ? undefined : block.animation}>
                    <BlockRenderer block={block} previewMode={previewMode} />
                  </AnimationWrapper>
                </BlockLayoutWrapper>
              </BlockErrorBoundary>
            </VisibilityWrapper>
          ))}
          </Box>
        </Box>
        </EditorBlockContextProvider>
        </SectionTypographyProvider>
      </AnimationWrapper>

      {/* Spacing visualization — full overlay on direct hover, subtle outline when child hovered */}
      {showSpacingOverlay && (
        <SpacingOverlay padding={sectionPaddingResolved} margin={ZERO_MARGIN} paddingTokens={sectionPaddingTokens} />
      )}
      {showSectionOutline && (
        <Box
          position="absolute"
          inset={0}
          pointerEvents="none"
          zIndex={1}
          border="1px dashed"
          borderColor="rgba(255, 255, 255, 0.15)"
          borderRadius="2px"
        />
      )}
    </Box>
    </SpacingVizActiveProvider>
  );
}
